import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { invoicesApi } from '../../api/endpoints';
import {
  InvoiceList,
  InvoiceStatusMap,
  InvoiceTypeStringMap,
  InvoiceStatusStringMap,
  BulkOperationResult,
} from '../../types';
import Select from '../../components/common/Select';
import { useOperations } from '../../contexts/OperationsContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  FiSearch,
  FiPlus,
  FiDownload,
  FiUpload,
  FiSend,
  FiX,
  FiFileText,
  FiCheckSquare,
  FiSquare,
  FiTrash2,
  FiAlertCircle,
  FiCheck,
  FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import FormField from '../../components/common/FormField';
import { SkeletonTable } from '../../components/common/Skeleton';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import DateRangePicker from '../../components/common/DateRangePicker';
import ColumnSettings from '../../components/common/ColumnSettings';

const InvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ops = useOperations();
  const [searchInput, setSearchInput] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkResult, setShowBulkResult] = useState<BulkOperationResult | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean; title: string; message: string; onConfirm: () => void}>({
    open: false, title: '', message: '', onConfirm: () => {},
  });

  // Create form state
  const [createData, setCreateData] = useState({
    purchaseOrderId: '',
    invoiceNumber: '',
    invoiceType: 0,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentTerms: '',
    currency: 'EUR',
    notes: '',
    items: [{ amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
  });
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formTouched, setFormTouched] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<InvoiceList>(invoicesApi.list, {
    initialSortBy: 'invoiceDate',
    initialSortDesc: true,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleTypeFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        invoiceType: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleRowClick = useCallback(
    (row: InvoiceList) => {
      navigate(`/invoices/${row.id}`);
    },
    [navigate]
  );

  // Selection
  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!data?.items) return;
    setSelectedIds((prev) => {
      if (prev.size === data.items.length) return new Set();
      return new Set(data.items.map((i) => i.id));
    });
  }, [data]);

  // Bulk submit with progress tracking
  const handleBulkSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDialog({
      open: true,
      title: t('invoices.bulkSubmitTitle'),
      message: t('invoices.bulkSubmitMessage', { count }),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setBulkSubmitting(true);
        const opId = ops.startOperation({
          type: 'bulk-submit',
          label: t('invoices.bulkSubmitProgress', { count }),
          progress: { current: 0, total: count },
        });

        try {
          ops.updateProgress(opId, 0, t('invoices.sendingToAmazonApi'));
          const response = await invoicesApi.bulkSubmit(Array.from(selectedIds));
          const result: BulkOperationResult = response.data;

          ops.completeOperation(opId, {
            success: result.successCount,
            failed: result.failedCount,
            errors: result.errors.map(e => ({ identifier: e.identifier, error: e.error })),
          });
          setShowBulkResult(result);
          setSelectedIds(new Set());
          refresh();
        } catch (error: any) {
          ops.failOperation(opId, error.response?.data?.message || t('invoices.bulkSubmitError'));
          toast.error(error.response?.data?.message || t('invoices.bulkSubmitError'));
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  }, [selectedIds, refresh, ops, t]);

  // Bulk download with progress
  const handleBulkDownload = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkDownloading(true);
    const count = selectedIds.size;
    const opId = ops.startOperation({
      type: 'bulk-download',
      label: t('invoices.bulkDownloadProgress', { count }),
      progress: { current: 0, total: count },
    });

    try {
      ops.updateProgress(opId, 0, t('invoices.generatingZip'));
      const response = await invoicesApi.bulkDownload(Array.from(selectedIds));
      ops.updateProgress(opId, count, t('invoices.downloadingFile'));

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktury_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      ops.completeOperation(opId, { success: count, failed: 0, errors: [] });
    } catch (error: any) {
      ops.failOperation(opId, error.response?.data?.message || t('invoices.downloadError'));
      toast.error(error.response?.data?.message || t('invoices.downloadError'));
    } finally {
      setBulkDownloading(false);
    }
  }, [selectedIds, ops, t]);

  // Export CSV
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response = await invoicesApi.exportCsv({
        ...query,
        ...filters,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktury_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('invoices.exportCsvSuccess'));
    } catch (error: any) {
      toast.error(t('invoices.exportCsvError'));
    } finally {
      setExporting(false);
    }
  }, [query, filters, t]);

  // Import CSV
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImporting(true);
      const opId = ops.startOperation({
        type: 'import',
        label: t('invoices.importFromFile', { filename: file.name }),
        progress: { current: 0, total: 1 },
      });

      try {
        ops.updateProgress(opId, 0, t('invoices.uploadingFile', { filename: file.name, size: (file.size / 1024).toFixed(1) }));
        const formData = new FormData();
        formData.append('file', file);
        const response = await invoicesApi.importCsv(formData);
        const result: BulkOperationResult = response.data;

        ops.completeOperation(opId, {
          success: result.successCount,
          failed: result.failedCount,
          errors: result.errors.map(e => ({ identifier: e.identifier, error: e.error })),
        });
        setShowBulkResult(result);
        refresh();
      } catch (error: any) {
        ops.failOperation(opId, error.response?.data?.message || t('invoices.importError'));
        toast.error(error.response?.data?.message || t('invoices.importError'));
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [refresh, ops, t]
  );

  // Create invoice
  const addItem = useCallback(() => {
    setCreateData((prev) => ({
      ...prev,
      items: [...prev.items, { amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
    }));
  }, []);

  const removeItem = useCallback((index: number) => {
    setCreateData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  }, []);

  const updateItem = useCallback((index: number, field: string, value: any) => {
    setCreateData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  }, []);

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!createData.purchaseOrderId.trim()) errors.purchaseOrderId = t('invoices.validation.purchaseOrderRequired');
    if (!createData.invoiceNumber.trim()) errors.invoiceNumber = t('invoices.validation.invoiceNumberRequired');
    if (!createData.invoiceDate) errors.invoiceDate = t('invoices.validation.invoiceDateRequired');
    const validItems = createData.items.filter(i => i.amazonProductId || i.description);
    if (validItems.length === 0) errors.items = t('invoices.validation.addAtLeastOneItem');
    validItems.forEach((item, idx) => {
      if (!item.description.trim()) errors[`item_${idx}_description`] = t('invoices.validation.descriptionRequired');
      if (item.quantity <= 0) errors[`item_${idx}_quantity`] = t('invoices.validation.quantityPositive');
      if (item.unitPrice <= 0) errors[`item_${idx}_unitPrice`] = t('invoices.validation.pricePositive');
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [createData, t]);

  const handleCreateSubmit = useCallback(async () => {
    setFormTouched(true);
    if (!validateForm()) {
      toast.warning(t('invoices.fixFormErrors'));
      return;
    }
    setCreating(true);
    try {
      await invoicesApi.create({
        ...createData,
        items: createData.items.filter((i) => i.amazonProductId || i.description),
      });
      toast.success(t('invoices.invoiceCreated'));
      setShowCreateModal(false);
      setCreateData({
        purchaseOrderId: '',
        invoiceNumber: '',
        invoiceType: 0,
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        paymentTerms: '',
        currency: 'EUR',
        notes: '',
        items: [{ amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
      });
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.createError'));
    } finally {
      setCreating(false);
    }
  }, [createData, refresh, validateForm, t]);

  const formatCurrency = (amount: number, currency?: string) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: currency || 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const itemsTotal = createData.items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const itemsTax = createData.items.reduce((sum, i) => sum + i.quantity * i.unitPrice * (i.taxRate / 100), 0);

  const columns = [
    {
      key: '_select',
      header: '',
      render: (row: InvoiceList) => (
        <span
          onClick={(e) => toggleSelect(row.id, e)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          {selectedIds.has(row.id) ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
        </span>
      ),
    },
    {
      key: 'invoiceNumber',
      header: t('invoices.invoiceNumber'),
      sortable: true,
    },
    {
      key: 'purchaseOrderNumber',
      header: t('invoices.poNumber'),
    },
    {
      key: 'invoiceType',
      header: t('invoices.type'),
      render: (row: InvoiceList) => {
        const label = InvoiceTypeStringMap[row.invoiceType] || row.invoiceType;
        const cls = row.invoiceType === 'CreditNote' ? 'badge badge-cancelled' :
                    row.invoiceType === 'DebitNote' ? 'badge badge-pending' :
                    'badge badge-active';
        return <span className={cls}>{label}</span>;
      },
    },
    {
      key: 'status',
      header: t('invoices.status'),
      sortable: true,
      render: (row: InvoiceList) => {
        const label = InvoiceStatusStringMap[row.status] || row.status;
        return <span className={`badge badge-${row.status === 'Draft' ? 'draft' : row.status === 'Submitted' ? 'pending' : row.status === 'Accepted' ? 'confirmed' : row.status === 'Paid' ? 'delivered' : row.status === 'Rejected' ? 'cancelled' : 'draft'}`}>{label}</span>;
      },
    },
    {
      key: 'invoiceDate',
      header: t('invoices.invoiceDate'),
      sortable: true,
      render: (row: InvoiceList) => formatDate(row.invoiceDate),
    },
    {
      key: 'totalAmount',
      header: t('invoices.amount'),
      sortable: true,
      render: (row: InvoiceList) => formatCurrency(row.totalAmount, row.currency),
    },
    {
      key: 'hasPdf',
      header: t('common.pdf'),
      render: (row: InvoiceList) =>
        row.hasPdf ? (
          <span className="text-success" title={t('invoices.pdfAvailable')}>
            <FiFileText />
          </span>
        ) : (
          <span style={{ opacity: 0.3 }}>-</span>
        ),
    },
  ];

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('invoices.title')}</h1>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={refresh} title={t('common.refresh')}>
            <FiRefreshCw />
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <FiPlus /> {t('invoices.newInvoice')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleImportClick}
            disabled={importing}
          >
            <FiUpload /> {importing ? t('invoices.importing') : t('invoices.importCsv')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={exporting}
          >
            <FiDownload /> {exporting ? t('invoices.exporting') : t('invoices.exportCsv')}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.invoicesXlsx, `faktury_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> {t('common.excel')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">
            <FiCheckSquare /> {t('common.selected')}: {selectedIds.size}
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleBulkSubmit}
            disabled={bulkSubmitting}
          >
            <FiSend /> {bulkSubmitting ? t('invoices.sending') : t('invoices.submitSelectedToAmazon')}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
          >
            <FiDownload /> {bulkDownloading ? t('invoices.downloading') : t('invoices.bulkDownload')}
          </button>
          <button
            className="btn btn-link btn-sm"
            onClick={() => setSelectedIds(new Set())}
          >
            <FiX /> {t('invoices.deselect')}
          </button>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('invoices.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters" style={{ display: 'flex', gap: '0.5rem' }}>
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              ...Object.entries(InvoiceStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder={t('common.allStatuses')}
          />
          <Select
            options={[
              { value: '', label: t('common.allTypes') },
              { value: '0', label: t('invoices.typeInvoice') },
              { value: '1', label: t('invoices.typeCreditNote') },
              { value: '2', label: t('invoices.typeDebitNote') },
            ]}
            value={String(filters.invoiceType ?? '')}
            onChange={handleTypeFilter}
            placeholder={t('common.allTypes')}
          />
          <button
            className="btn btn-link btn-sm"
            onClick={toggleSelectAll}
            title={t('invoices.selectDeselectAll')}
          >
            {selectedIds.size === (data?.items?.length || 0) && selectedIds.size > 0
              ? <><FiCheckSquare /> {t('invoices.deselect')}</>
              : <><FiSquare /> {t('invoices.select')}</>
            }
          </button>
          <ColumnSettings
            columns={columns.filter(c => c.key !== '_select')}
            storageKey="invoices"
            onColumnsChange={setVisibleColumnKeys}
          />
        </div>
      </div>

      {/* Date range filter */}
      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={String(filters.dateFrom ?? '')}
          dateTo={String(filters.dateTo ?? '')}
          onDateFromChange={(v) => setFilters((p: any) => ({ ...p, dateFrom: v || undefined }))}
          onDateToChange={(v) => setFilters((p: any) => ({ ...p, dateTo: v || undefined }))}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={10} cols={8} />
      ) : (
        <DataTable
          columns={visibleColumnKeys.length > 0 ? columns.filter(c => visibleColumnKeys.includes(c.key)) : columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
        />
      )}

      {/* Bulk result modal */}
      {showBulkResult && (
        <div className="modal-overlay" onClick={() => setShowBulkResult(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{t('invoices.operationResult')}</h2>
              <button className="btn btn-icon" onClick={() => setShowBulkResult(null)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="bulk-result-summary">
                <div className="bulk-stat bulk-stat-success">
                  <FiCheck /> {t('common.success')}: {showBulkResult.successCount}
                </div>
                <div className="bulk-stat bulk-stat-error">
                  <FiAlertCircle /> {t('invoices.errors')}: {showBulkResult.failedCount}
                </div>
                <div className="bulk-stat">
                  {t('common.total')}: {showBulkResult.totalCount}
                </div>
              </div>
              {showBulkResult.errors.length > 0 && (
                <div className="bulk-errors" style={{ marginTop: '1rem' }}>
                  <h4>{t('invoices.errorDetails')}:</h4>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t('invoices.identifier')}</th>
                          <th>{t('common.error')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showBulkResult.errors.map((err, i) => (
                          <tr key={i}>
                            <td>{err.identifier}</td>
                            <td>{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowBulkResult(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>{t('invoices.newInvoice')}</h2>
              <button className="btn btn-icon" onClick={() => setShowCreateModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormField label={t('invoices.purchaseOrderId')} required error={formTouched ? formErrors.purchaseOrderId : undefined} hint={t('invoices.purchaseOrderIdHint')}>
                  <input
                    type="text"
                    className="form-control"
                    value={createData.purchaseOrderId}
                    onChange={(e) => setCreateData((p) => ({ ...p, purchaseOrderId: e.target.value }))}
                    placeholder={t('invoices.purchaseOrderIdPlaceholder')}
                  />
                </FormField>
                <FormField label={t('invoices.invoiceNumber')} required error={formTouched ? formErrors.invoiceNumber : undefined} hint={t('invoices.invoiceNumberHint')}>
                  <input
                    type="text"
                    className="form-control"
                    value={createData.invoiceNumber}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceNumber: e.target.value }))}
                    placeholder="FV/2026/03/0001"
                  />
                </FormField>
                <FormField label={t('invoices.documentType')}>
                  <select
                    className="form-control"
                    value={createData.invoiceType}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceType: Number(e.target.value) }))}
                  >
                    <option value={0}>{t('invoices.typeInvoice')}</option>
                    <option value={1}>{t('invoices.typeCreditNote')}</option>
                    <option value={2}>{t('invoices.typeDebitNote')}</option>
                  </select>
                </FormField>
                <FormField label={t('invoices.currency')}>
                  <select
                    className="form-control"
                    value={createData.currency}
                    onChange={(e) => setCreateData((p) => ({ ...p, currency: e.target.value }))}
                  >
                    <option value="EUR">EUR</option>
                    <option value="PLN">PLN</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </FormField>
                <FormField label={t('invoices.invoiceDate')} required error={formTouched ? formErrors.invoiceDate : undefined}>
                  <input
                    type="date"
                    className="form-control"
                    value={createData.invoiceDate}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceDate: e.target.value }))}
                  />
                </FormField>
                <FormField label={t('invoices.dueDate')} hint={t('invoices.dueDateHint')}>
                  <input
                    type="date"
                    className="form-control"
                    value={createData.dueDate}
                    onChange={(e) => setCreateData((p) => ({ ...p, dueDate: e.target.value }))}
                  />
                </FormField>
                <FormField label={t('invoices.paymentTerms')} hint={t('invoices.paymentTermsHint')}>
                  <input
                    type="text"
                    className="form-control"
                    value={createData.paymentTerms}
                    onChange={(e) => setCreateData((p) => ({ ...p, paymentTerms: e.target.value }))}
                    placeholder="Net 30"
                  />
                </FormField>
                <FormField label={t('invoices.notes')} hint={t('invoices.notesHint')}>
                  <input
                    type="text"
                    className="form-control"
                    value={createData.notes}
                    onChange={(e) => setCreateData((p) => ({ ...p, notes: e.target.value }))}
                    placeholder={t('invoices.notesPlaceholder')}
                  />
                </FormField>
              </div>
              {formTouched && formErrors.items && (
                <div className="field-error" style={{ marginTop: '8px' }}>
                  <FiAlertCircle size={12} /> {formErrors.items}
                </div>
              )}

              {/* Invoice items */}
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>{t('invoices.invoiceItems')}</h3>
                  <button className="btn btn-secondary btn-sm" onClick={addItem}>
                    <FiPlus /> {t('invoices.addItem')}
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('invoices.amazonProductId')}</th>
                        <th>{t('invoices.description')}</th>
                        <th style={{ width: '80px' }}>{t('invoices.quantity')}</th>
                        <th style={{ width: '100px' }}>{t('invoices.unitPrice')}</th>
                        <th style={{ width: '80px' }}>{t('invoices.vatPercent')}</th>
                        <th style={{ width: '100px' }}>{t('invoices.value')}</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {createData.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.amazonProductId}
                              onChange={(e) => updateItem(idx, 'amazonProductId', e.target.value)}
                              placeholder="ASIN / ID"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.description}
                              onChange={(e) => updateItem(idx, 'description', e.target.value)}
                              placeholder={t('invoices.productDescription')}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.quantity}
                              min={1}
                              onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.unitPrice}
                              min={0}
                              step={0.01}
                              onChange={(e) => updateItem(idx, 'unitPrice', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.taxRate}
                              min={0}
                              max={100}
                              onChange={(e) => updateItem(idx, 'taxRate', Number(e.target.value))}
                            />
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 500 }}>
                            {(item.quantity * item.unitPrice).toFixed(2)}
                          </td>
                          <td>
                            {createData.items.length > 1 && (
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={() => removeItem(idx)}
                                title={t('invoices.removeItem')}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>{t('invoices.net')}:</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{itemsTotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right' }}>{t('invoices.vat')}:</td>
                        <td style={{ textAlign: 'right' }}>{itemsTax.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>{t('invoices.gross')}:</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{(itemsTotal + itemsTax).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateSubmit}
                disabled={creating}
              >
                {creating ? t('invoices.creating') : t('invoices.createInvoice')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={t('invoices.send')}
        variant="warning"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        loading={bulkSubmitting}
      />
    </div>
  );
};

export default InvoicesPage;
